/**
 * Opt-in micro-profiler for the visualization workspace. Answers the practical question
 * "when I zoom, what's slow — crunching the cube or rendering the chart?".
 *
 * Two things get measured:
 *   - **crunch**: the heavy transformer work — `cube.filter` (`filterCubeByPathAndGrade`) and
 *     `cube.aggregate` (`aggregateTaxaAllBuckets`). These run *inside* `useSelector` during the
 *     React render, memoised per `(sourceId, optsKey)`, so they only fire on an actual zoom.
 *   - **render**: each sink's React commit duration, via a `<Profiler onRender>` in
 *     `SinkContainer`. NOTE: because the selectors run during render, a sink's render duration
 *     *includes* its crunch time. To isolate the pure paint, subtract the
 *     matching `cube.*` measurements from the `render …` measurement for the same zoom.
 *
 * Off by default (zero overhead beyond one cached boolean check). Enable from the console:
 *
 *   vizPerf.enable()    // persists in localStorage; survives reloads
 *   …zoom around…
 *   vizPerf.report()    // console.table: count / last / avg / max ms per label
 *   vizPerf.reset()
 *   vizPerf.disable()
 *
 * When enabled, every measurement is also emitted as a `performance.measure('[viz] <label>')`
 * entry, so it shows up in the browser DevTools Performance timeline alongside React's own marks.
 */

type Stat = { count: number; totalMs: number; lastMs: number; maxMs: number }

const stats = new Map<string, Stat>()
let enabled: boolean | undefined

function isEnabled(): boolean {
  if (enabled !== undefined) return enabled
  try {
    enabled =
      typeof window !== 'undefined' &&
      ((window as unknown as { __vizPerf?: boolean }).__vizPerf === true ||
        window.localStorage?.getItem('vizPerf') === '1')
  } catch {
    enabled = false
  }
  return !!enabled
}

/** Public read of the opt-in flag, for call sites that want to gate their own diagnostics. */
export function isVizPerfEnabled(): boolean {
  return isEnabled()
}

export function setVizPerf(on: boolean): void {
  enabled = on
  try {
    if (typeof window !== 'undefined') window.localStorage?.setItem('vizPerf', on ? '1' : '0')
  } catch {
    /* localStorage may be unavailable (private mode / SSR) — toggle still applies in-memory */
  }
  console.info(`[viz-perf] ${on ? 'enabled — zoom around, then vizPerf.report()' : 'disabled'}`)
}

function record(label: string, ms: number, meta?: Record<string, unknown>): void {
  const s = stats.get(label) ?? { count: 0, totalMs: 0, lastMs: 0, maxMs: 0 }
  s.count++
  s.totalMs += ms
  s.lastMs = ms
  s.maxMs = Math.max(s.maxMs, ms)
  stats.set(label, s)
  console.debug(`[viz-perf] ${label}: ${ms.toFixed(1)}ms`, meta ?? '')
  try {
    performance.measure?.(`[viz] ${label}`, { start: performance.now() - ms, duration: ms })
  } catch {
    /* User Timing L3 options form unsupported — the console.debug above still stands */
  }
}

/** Time the synchronous `fn`. Near-zero cost (and no `performance.now()` call) when disabled. */
export function profile<T>(label: string, fn: () => T, meta?: Record<string, unknown>): T {
  if (!isEnabled()) return fn()
  const t0 = performance.now()
  try {
    return fn()
  } finally {
    record(label, performance.now() - t0, meta)
  }
}

/** Feed by `SinkContainer`'s `<Profiler onRender>`. */
export function recordRender(id: string, phase: string, actualDurationMs: number): void {
  if (!isEnabled()) return
  record(`render ${id} (${phase})`, actualDurationMs)
}

/** Dump a sorted (slowest-avg-first) summary table and return the rows. */
export function vizPerfReport(): Array<{ label: string; count: number; lastMs: number; avgMs: number; maxMs: number }> {
  const rows = [...stats.entries()]
    .map(([label, s]) => ({
      label,
      count: s.count,
      lastMs: +s.lastMs.toFixed(1),
      avgMs: +(s.totalMs / s.count).toFixed(1),
      maxMs: +s.maxMs.toFixed(1)
    }))
    .sort((a, b) => b.avgMs - a.avgMs)
  console.table(rows)
  return rows
}

export function vizPerfReset(): void {
  stats.clear()
}

// Expose a console handle. Guarded so it's a no-op under SSR / tests without a window.
if (typeof window !== 'undefined') {
  ;(window as unknown as { vizPerf?: unknown }).vizPerf = {
    enable: () => setVizPerf(true),
    disable: () => setVizPerf(false),
    report: vizPerfReport,
    reset: vizPerfReset
  }
}
